import React from 'react'
import { View, StyleSheet, TouchableOpacity, Image } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Text, Icon } from '@components'
import { useLanguage } from '@context'
import { NearbyContact, useInitiateIndividualConversation } from '@hooks'
import { defaultImage } from '@assets'
import dayjs from '../../../../utils/dayjs'

interface NearbyContactItemProps {
  contact: NearbyContact
  onPress: () => void
}

export const NearbyContactItem: React.FC<NearbyContactItemProps> = ({ contact, onPress }) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const navigation = useNavigation()

  const { mutateAsync: initiateConversation } = useInitiateIndividualConversation()

  const isPrivate = contact.scope === 'PRIVATE'
  const scopeColor = isPrivate ? theme.colors.green : theme.colors.primary
  const scopeIcon = isPrivate ? 'lock' : 'work'
  const scopeLabel = isPrivate ? t('nearby.scopePrivate') : t('nearby.scopePro')

  const profession = contact.professions.length > 0 ? contact.professions.join(', ') : null
  const timeAgo = contact.lastLocationAt ? dayjs(contact.lastLocationAt).fromNow() : null

  const handleMessage = async () => {
    if (!contact.accountId) return
    try {
      const conversation = await initiateConversation({ participantAccountId: contact.accountId })
      ;(navigation as any).navigate('Messages', {
        screen: 'Conversation',
        params: { conversationId: conversation.id },
      })
    } catch (_) {}
  }

  return (
    <TouchableOpacity style={styles.container} onPress={onPress} activeOpacity={0.7}>
      <Image
        source={contact.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
        style={styles.avatar}
      />

      <View style={styles.info}>
        <View style={styles.nameRow}>
          <Text type="bodyBold" numberOfLines={1} style={styles.name}>
            {contact.fullName}
          </Text>
          {contact.scope && (
            <View style={[styles.scopeBadge, { borderColor: scopeColor }]}>
              <Icon name={scopeIcon} size={11} color={scopeColor} />
              <Text type="bodyExtraSmall" style={[styles.scopeText, { color: scopeColor }]}>
                {scopeLabel}
              </Text>
            </View>
          )}
        </View>

        {profession && (
          <Text type="bodySmall" color={theme.colors.primary} numberOfLines={1}>
            {profession}
          </Text>
        )}

        {timeAgo && (
          <View style={styles.timeRow}>
            <Icon name="clock" size={12} color={theme.colors.greyBlue} />
            <Text type="bodyExtraSmall" color={theme.colors.greyBlue}>
              {timeAgo}
            </Text>
          </View>
        )}
      </View>

      {contact.accountId && (
        <TouchableOpacity style={styles.messageButton} onPress={handleMessage} activeOpacity={0.7}>
          <Icon name="messageCircle" size={24} color={theme.colors.primary} />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 16,
      paddingVertical: 12,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.grey,
      gap: 12,
    },
    avatar: {
      width: 58,
      height: 58,
      borderRadius: 29,
      backgroundColor: theme.colors.grey,
    },
    info: {
      flex: 1,
      gap: 3,
    },
    nameRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      flexWrap: 'wrap',
    },
    name: {
      flexShrink: 1,
    },
    scopeBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      borderWidth: 1,
      borderRadius: 10,
      paddingHorizontal: 6,
      paddingVertical: 2,
    },
    scopeText: {
      fontSize: 11,
    },
    timeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    messageButton: {
      padding: 8,
    },
  })
